/**
 * @file RepRequestHistory.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The record of price requests sent to reps (Cam, 28 Oct 2026 —
 *              P1: "Regarding xlsx that goes to reps, can we have a record of
 *              what was sent to who when please, including the ability to
 *              resend?"; q01 "Offer both"). An embeddable list — the coming Rep
 *              Pricing screen and the rep's own pricing view sit it where they
 *              need it; for now Pricing & Costing opens it from its header
 *              ("Sent to reps", RepRequestHistoryModal below).
 *
 *              Each send: when, who sent it, to which rep and address, how many
 *              customers / requests / lines, second check or not, a resend or
 *              a send again, and — for sends before 28 Oct, read from the email
 *              log — "from the email log". Expand a send for its requests and
 *              lines as they were sent. Download its spreadsheet as sent.
 *              Resend… — the same email and spreadsheet again, to the same
 *              (editable) recipients, recorded as a new send. Send again —
 *              today's figures, through the ordinary Send to reps window
 *              (`onSendAgain(send, lineIds)`; greyed when the host has no such
 *              window). Every button is always there, greyed with the reason.
 *
 *              Props: { orderLineId?, orderId?, repId? (fixed filters),
 *              onSendAgain?, canResend? (holds 'orders.price'), pageSize? }.
 *              API: /api/rep-price-requests/sends …
 *
 *              1.1.0 (Cam, 28 Oct 2026 — the office's Rep Pricing view): the
 *              resend window (ResendDialog, resendSend) and the download
 *              (downloadSendWorkbook) are exported, so Rep Pricing's Waiting
 *              tab offers them on the send its requests went in; a fixed
 *              `repId` that changes is followed (the view's rep list).
 */

import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import ConfirmDialog from './ConfirmDialog';
import SenderSelect from './SenderSelect';
import LoadingSpinner from './LoadingSpinner';
import PaginationControls from './PaginationControls';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { formatDateTimeLondon, formatDate } from '../utils/dateFormatters';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity, formatCount } from '../utils/numberFormatters';
import './RepRequestHistory.css';

const HOW_LABEL = { resend: 'Resent', send_again: 'Asked again' };

/** "5.00 T", "40 × 25kg bag", "12.00" — a request's quantity in its own unit. */
export function qtyWithUnit(q, unit) {
  if (q === null || q === undefined || q === '') return '';
  if (!unit) return formatQuantity(q);
  if (unit === 'T') return `${formatQuantity(q)} T`;
  return `${formatCount(q)} × ${unit}`;
}

/** What a send asked, in a few words: "3 customers · 4 requests · 6 lines". */
export function sendSummary(s) {
  const n = (k, one, many) => (k === null || k === undefined ? null : `${k} ${k === 1 ? one : many}`);
  return [n(s.customer_count, 'customer', 'customers'), n(s.request_count, 'request', 'requests'), n(s.line_count, 'line', 'lines')]
    .filter(Boolean).join(' · ');
}

/** Why a send cannot be resent / downloaded / asked again, or null. */
export function actionBlocks(s, { canResend = true, canSendAgain = true } = {}) {
  const resend = !canResend ? 'Asking reps for prices needs permission to price sales orders'
    : s.kind === 'email_log' && !s.has_workbook && !s.subject ? 'Nothing of this email was kept to send again'
      : null;
  const download = s.has_workbook ? null : 'No spreadsheet was kept for this send (it may have gone without one)';
  const again = !canSendAgain ? 'Open this from Pricing & Costing to ask again with today\'s figures'
    : !canResend ? 'Asking reps for prices needs permission to price sales orders'
      : !s.line_count ? 'None of its order lines is known' : null;
  return { resend, download, again };
}

function RepRequestHistory({ orderLineId = null, orderId = null, repId: fixedRep = null, onSendAgain = null, canResend = true, pageSize = 25 }) {
  const [filters, setFilters] = useState({ repId: fixedRep ? String(fixedRep) : '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ sends: [], total: 0, ready: true, not_ready_reason: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reps, setReps] = useState([]);
  const [open, setOpen] = useState(null);         // the send expanded
  const [detail, setDetail] = useState({});        // id → detail
  const [resending, setResending] = useState(null); // { send, to, cc, sendAs }
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  // A fixed rep that changes (Rep Pricing's rep list) is followed.
  useEffect(() => {
    const want = fixedRep ? String(fixedRep) : '';
    setFilters(f => (f.repId === want || (!fixedRep && f.repId) ? f : { ...f, repId: want }));
  }, [fixedRep]);

  useEffect(() => {
    fetch(`${API_URL}/api/orders/sales-reps?includeArchived=true`, { credentials: 'include' })
      .then(r => r.json()).then(b => setReps(Array.isArray(b.reps) ? b.reps : [])).catch(() => setReps([]));
  }, []);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams({ page: String(p), limit: String(pageSize) });
      if (filters.repId) q.set('repId', filters.repId);
      if (filters.from) q.set('from', filters.from);
      if (filters.to) q.set('to', filters.to);
      if (orderLineId) q.set('orderLineId', String(orderLineId));
      if (orderId) q.set('orderId', String(orderId));
      const res = await fetch(`${API_URL}/api/rep-price-requests/sends?${q}`, { credentials: 'include' });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'The sends could not be read');
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page, orderLineId, orderId, pageSize]);

  useEffect(() => { load(page); }, [filters, page, orderLineId, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const expand = async (s) => {
    if (open === s.id) { setOpen(null); return; }
    setOpen(s.id);
    if (detail[s.id]) return;
    try {
      const res = await fetch(`${API_URL}/api/rep-price-requests/sends/${encodeURIComponent(s.id)}`, { credentials: 'include' });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Not found');
      setDetail(prev => ({ ...prev, [s.id]: body.send }));
    } catch (err) {
      setDetail(prev => ({ ...prev, [s.id]: { error: err.message } }));
    }
  };

  const download = async (s) => {
    try {
      await downloadSendWorkbook(s);
    } catch (err) {
      setError(err.message);
    }
  };

  const sendAgain = async (s) => {
    let d = detail[s.id];
    if (!d) {
      const res = await fetch(`${API_URL}/api/rep-price-requests/sends/${encodeURIComponent(s.id)}`, { credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      d = body.send || null;
    }
    const ids = (d?.lines || []).filter(l => l.still_on_order).map(l => l.order_line_id);
    if (!ids.length) { setError('None of this send\'s order lines is still on an order.'); return; }
    onSendAgain(s, ids);
  };

  const doResend = async () => {
    const r = resending;
    setResending(null);
    setBusy(true);
    setError(null);
    try {
      setNotice(await resendSend(r));
      setPage(1);
      load(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const total = data.total || 0;
  const repOptions = [{ value: '', label: 'All reps' }, ...reps.map(r => ({ value: String(r.id), label: r.name + (r.status && r.status !== 'ACTIVE' ? ' (archived)' : '') }))];

  return (
    <div className="rep-history">
      {!data.ready && data.not_ready_reason && (
        <p className="rh-note warn"><span className="material-symbols-rounded">construction</span>
          {data.not_ready_reason}. Sends before then are listed from the email log; new ones are not kept here until it has run.</p>
      )}
      {error && <p className="rh-note err"><span className="material-symbols-rounded">error</span>{error}</p>}
      {notice && <p className="rh-note ok"><span className="material-symbols-rounded">check_circle</span>{notice}</p>}
      <div className="rh-filters">
        {!fixedRep && (
          <div className="filter-group"><label>Rep</label>
            <Dropdown options={repOptions} value={filters.repId} onChange={(v) => { setPage(1); setFilters(f => ({ ...f, repId: v })); }} />
          </div>
        )}
        <div className="filter-group"><DateInput label="Sent from" value={filters.from} onChange={(e) => { setPage(1); setFilters(f => ({ ...f, from: e.target.value })); }} /></div>
        <div className="filter-group"><DateInput label="Sent to" value={filters.to} onChange={(e) => { setPage(1); setFilters(f => ({ ...f, to: e.target.value })); }} /></div>
        <span className="pc-muted rh-count">{total} send{total === 1 ? '' : 's'}</span>
      </div>
      {loading ? <LoadingSpinner /> : (
        <div className="table-container rh-table-wrap">
          <table className="data-table rh-table">
            <thead>
              <tr><th></th><th>Sent</th><th>By</th><th>Rep</th><th>To</th><th>What</th><th></th><th className="actions-col">Actions</th></tr>
            </thead>
            <tbody>
              {data.sends.length === 0 && (
                <tr><td colSpan={8} className="pc-muted">Nothing sent to reps{orderLineId || orderId ? ' for this' : ''} yet.</td></tr>
              )}
              {data.sends.map(s => {
                const blocks = actionBlocks(s, { canResend, canSendAgain: !!onSendAgain });
                const d = detail[s.id];
                return (
                  <React.Fragment key={s.id}>
                    <tr className={`expandable-row ${open === s.id ? 'expanded' : ''}`} onClick={() => expand(s)}>
                      <td className="rh-chev"><span className="material-symbols-rounded">{open === s.id ? 'expand_less' : 'expand_more'}</span></td>
                      <td>{formatDateTimeLondon(s.sent_at)}</td>
                      <td>{s.sent_by || '—'}</td>
                      <td>{s.rep_name || <span className="pc-muted">—</span>}</td>
                      <td className="rh-to" title={s.cc ? `Cc ${s.cc}` : ''}>{s.to}</td>
                      <td>{sendSummary(s) || '—'}</td>
                      <td className="rh-badges">
                        {s.second_check && <span className="rh-badge">Second check</span>}
                        {HOW_LABEL[s.how] && <span className="rh-badge" title={s.resend_of ? `Of the send of ${s.resend_of}` : ''}>{HOW_LABEL[s.how]}</span>}
                        {s.kind === 'email_log' && <span className="rh-badge muted" title="Sent before sends were recorded here — read from the email log">From the email log</span>}
                        {s.email_status && !['sent', 'delivered'].includes(s.email_status) && <span className="rh-badge err">{s.email_status}</span>}
                      </td>
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                        <div className="actions-cell">
                          <button type="button" className={`btn-icon ${blocks.download ? 'btn-disabled' : ''}`} disabled={!!blocks.download}
                            title={blocks.download || `Download ${s.workbook_filename || 'the spreadsheet'} as sent`} onClick={() => download(s)}>
                            <span className="material-symbols-rounded">download</span>
                          </button>
                          <button type="button" className={`btn-icon ${blocks.resend || busy ? 'btn-disabled' : ''}`} disabled={!!blocks.resend || busy}
                            title={blocks.resend || 'Resend — the same email and spreadsheet again'}
                            onClick={() => setResending({ send: s, to: s.to || '', cc: s.cc || '', sendAs: 'me' })}>
                            <span className="material-symbols-rounded">forward_to_inbox</span>
                          </button>
                          <button type="button" className={`btn-icon ${blocks.again ? 'btn-disabled' : ''}`} disabled={!!blocks.again}
                            title={blocks.again || 'Send again — today\'s figures, checked before it goes'} onClick={() => sendAgain(s)}>
                            <span className="material-symbols-rounded">restart_alt</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    {open === s.id && (
                      <tr className="expanded-content expanded">
                        <td colSpan={8}><SendDetail send={d} summary={s} /></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {total > pageSize && (
        <PaginationControls pagination={{ page, limit: pageSize, total, totalPages: Math.max(1, Math.ceil(total / pageSize)) }} onPageChange={setPage} />
      )}
      <ResendDialog resending={resending} onChange={setResending} onConfirm={doResend} onCancel={() => setResending(null)} />
    </div>
  );
}


/** Download a send's spreadsheet as it went (throws with why). */
export async function downloadSendWorkbook(s) {
  const res = await fetch(`${API_URL}/api/rep-price-requests/sends/${encodeURIComponent(s.id)}/workbook`, { credentials: 'include' });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = s.workbook_filename || 'Price request.xlsx';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Resend a send as it went: { send, to, cc, sendAs } → the server's message (throws with why). */
export async function resendSend(r) {
  const res = await fetch(`${API_URL}/api/rep-price-requests/sends/${encodeURIComponent(r.send.id)}/resend`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ to: r.to, cc: r.cc, sendAs: r.sendAs })
  });
  const body = await res.json();
  if (!res.ok || !body.success) throw new Error(body.error || 'Not sent');
  return body.message || 'Resent';
}

/**
 * The resend window: the same email (and spreadsheet) again, to the same
 * (editable) recipients. `resending` = { send, to, cc, sendAs } or null.
 */
export function ResendDialog({ resending, onChange, onConfirm, onCancel }) {
  return (
    <ConfirmDialog
      isOpen={!!resending}
      title="Resend to the rep"
      type="info"
      confirmText="Resend"
      confirmIcon="forward_to_inbox"
      confirmDisabled={!!resending && !String(resending.to || '').trim()}
      confirmDisabledReason="Who should it go to?"
      className="rh-resend"
      message={resending && (
        <div className="rh-resend-body">
          <p>Sends the same email{resending.send.has_workbook ? ' and spreadsheet' : ''} as on {formatDateTimeLondon(resending.send.sent_at)} again — unchanged. For today's figures use Send again instead.</p>
          <label>To<Input value={resending.to} onChange={(e) => onChange(r => ({ ...r, to: e.target.value }))} /></label>
          <label>Cc<Input value={resending.cc} placeholder="Optional" onChange={(e) => onChange(r => ({ ...r, cc: e.target.value }))} /></label>
          <label>Send from<SenderSelect value={resending.sendAs} onChange={(v) => onChange(r => ({ ...r, sendAs: v }))} /></label>
        </div>
      )}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

/** One send opened: its requests (recorded sends) or its lines (all). */
function SendDetail({ send, summary }) {
  if (!send) return <LoadingSpinner />;
  if (send.error) return <p className="rh-note err">{send.error}</p>;
  const reqs = Array.isArray(send.requests) ? send.requests : [];
  return (
    <div className="rh-detail">
      <p className="rh-subject"><b>{send.subject}</b>{send.cc ? <span className="pc-muted"> · Cc {send.cc}</span> : null}</p>
      {reqs.length > 0 ? (
        <table className="line-items-table rh-lines">
          <thead><tr><th>Customer</th><th>Product</th><th>Delivery</th><th>Orders</th><th>Quantity</th><th>Cost shown</th><th>Earlier sales shown</th></tr></thead>
          <tbody>
            {reqs.map((r, i) => (
              <tr key={i}>
                <td>{r.customer?.account_no ? <span className="account-badge-inline">{r.customer.account_no}</span> : null} {r.customer?.name}</td>
                <td>{r.product?.name}{r.product?.code ? <span className="pc-muted"> {r.product.code}</span> : null}{(r.product?.additives || []).length ? <span className="pc-muted"> + {r.product.additives.join(' + ')}</span> : null}</td>
                <td>{r.delivery_method?.label || '—'}</td>
                <td>{(r.orders || []).map(o => `${o.order_number}${o.delivery_date ? ` (${formatDate(o.delivery_date)})` : ''}`).join(', ')}</td>
                <td>{qtyWithUnit(r.quantity, r.unit)}</td>
                <td>{r.cost == null ? '—' : typeof r.cost === 'object' ? `${formatPrice(r.cost.min)}–${formatPrice(r.cost.max)}` : formatPrice(r.cost)}</td>
                <td>{(r.previous_sales || []).map((p, k) => (
                  <div key={k}>{formatPrice(p.price)} {p.date ? formatDate(p.date) : ''}{p.cost != null ? <span className="pc-muted"> · cost {formatPrice(p.cost)}{p.margin != null ? ` · margin ${formatPrice(p.margin)}` : ''}</span> : null}</div>
                ))}{r.earlier ? <div className="pc-muted">Their earlier price {formatPrice(r.earlier.price)}</div> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="line-items-table rh-lines">
          <thead><tr><th>Order</th><th>Customer</th><th>Product</th><th>Quantity</th><th>Delivery date</th><th></th></tr></thead>
          <tbody>
            {(send.lines || []).map(l => (
              <tr key={l.order_line_id}>
                <td>{l.state?.order_number || '—'}</td>
                <td>{l.state?.account_no ? <span className="account-badge-inline">{l.state.account_no}</span> : null} {l.state?.customer || '—'}</td>
                <td>{l.state?.product_name || l.state?.product_code || '—'}</td>
                <td>{l.state?.quantity == null ? '' : formatQuantity(l.state.quantity)}</td>
                <td>{l.state?.delivery_date ? formatDate(l.state.delivery_date) : ''}</td>
                <td className="pc-muted">{!l.still_on_order ? 'No longer on an order' : l.state?.as_now ? 'As it is now' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {summary.kind === 'email_log' && <p className="pc-muted">Sent before sends were recorded here: its lines are read from the orders' history and shown as they are now.</p>}
      {(send.resends || []).length > 0 && (
        <p className="pc-muted">Since: {send.resends.map(x => `${HOW_LABEL[x.how] || 'Sent'} ${formatDateTimeLondon(x.sent_at)} by ${x.sent_by || '—'} to ${x.to}`).join(' · ')}</p>
      )}
      {send.body && (
        <details className="rh-body"><summary>The email as sent</summary><pre>{send.body}</pre></details>
      )}
    </div>
  );
}

/**
 * The list in a window — Pricing & Costing's "Sent to reps" (until the Rep
 * Pricing screen holds it). `onSendAgain(send, lineIds)` closes it and opens
 * the Send to reps window on those lines.
 */
export function RepRequestHistoryModal({ onClose, onSendAgain, canResend }) {
  return (
    <Modal isOpen onClose={onClose} title="Sent to reps" size="xl" className="rep-history-modal"
      footer={<HistoryFooter onClose={onClose} />}>
      <RepRequestHistory onSendAgain={onSendAgain} canResend={canResend} />
    </Modal>
  );
}

function HistoryFooter({ onClose }) {
  useSlideOutButton([]);
  return (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Close</span>
        </button>
      </div>
    </div>
  );
}

export default RepRequestHistory;
